import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MarkdownField } from "@/components/ui/markdown-field";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { getScriptureReferences, createScriptureNote } from "@/lib/bible-api";
import { Loader2, Plus, RefreshCcw } from "lucide-react";
import type { ScriptureReference } from "@shared/types/supabase";

interface ScriptureReferencePanelProps {
  documentId?: number;
  onNoteSaved?: () => void;
  onInsert?: (payload: { reference: string; text: string }) => void;
}

export function ScriptureReferencePanel({ documentId, onNoteSaved, onInsert }: ScriptureReferencePanelProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [references, setReferences] = useState<ScriptureReference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReference, setSelectedReference] = useState<ScriptureReference | null>(null);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState("");
  
  const fetchReferences = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getScriptureReferences();
      setReferences(data);
    } catch (err) {
      console.error("Error fetching scripture references:", err);
      setError("Failed to load references");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchReferences();
  }, []);
  
  const handleRefresh = () => {
    fetchReferences();
  };
  
  const handleSelectReference = (reference: ScriptureReference) => {
    setSelectedReference(reference);
  };
  
  const handleStartAddNote = (reference: ScriptureReference) => {
    setSelectedReference(reference);
    setIsAddingNote(true);
  };
  
  const handleCancelAddNote = () => {
    setIsAddingNote(false);
    setNewNote("");
  };
  
  const handleSaveNote = async () => {
    if (!newNote.trim()) {
      toast({
        title: "Note is empty",
        description: "Please enter a note to save.",
        variant: "destructive",
      });
      return;
    }
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to add notes.",
        variant: "destructive",
      });
      return;
    }
    if (!selectedReference) {
      toast({
        title: "No passage selected",
        description: "Choose a saved reference first.",
        variant: "destructive",
      });
      return;
    }

    try {
      await createScriptureNote({
        content: newNote.trim(),
        user_id: user.id,
        parent_id: selectedReference.id,
        parent_type: "scripture",
      });

      toast({
        title: "Note added",
        description: "Your note has been saved successfully.",
      });

      setIsAddingNote(false);
      setNewNote("");
      setSelectedReference(null);
      onNoteSaved?.();
    } catch (err) {
      console.error("Error saving note:", err);
      toast({
        title: "Failed to save note",
        description: err instanceof Error ? err.message : "Please try again later.",
        variant: "destructive",
      });
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-destructive mb-4">{error}</p>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCcw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }
  
  if (references.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">No scripture references found.</p>
        <p className="mb-4">Start by searching for a verse in the Search tab.</p>
      </div>
    );
  }
  
  const formatReference = (ref: ScriptureReference): string => {
    return `${ref.book} ${ref.chapter}:${ref.verse_start}${ref.verse_end > ref.verse_start ? '-' + ref.verse_end : ''}`;
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Your References</h3>
        <Button variant="ghost" size="sm" onClick={handleRefresh}>
          <RefreshCcw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      <div className="space-y-3">
        {references.map(reference => (
          <Card key={reference.id} className="overflow-hidden">
            <CardHeader className="py-3 px-4">
                <div className="flex justify-between items-center gap-2">
                  <CardTitle className="text-sm font-medium">
                    {formatReference(reference)}
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    {onInsert && reference.text && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          onInsert({
                            reference: formatReference(reference),
                            text: reference.text || "",
                          })
                        }
                      >
                        Insert
                      </Button>
                    )}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" onClick={() => handleSelectReference(reference)}>
                          <Plus className="w-4 h-4 mr-1" />
                          Note
                        </Button>
                      </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Note for {formatReference(reference)}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="prose prose-sm dark:prose-invert">
                        <p>{reference.text}</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="note">Your Note</Label>
                        <MarkdownField
                          id="note"
                          placeholder="Add your thoughts or insights about this verse..."
                          value={newNote}
                          onValueChange={setNewNote}
                        />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={handleCancelAddNote}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveNote} disabled={!newNote.trim()}>
                          Save Note
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                  </div>
                </div>
              <CardDescription className="text-xs mt-1">
                {reference.translation || "ESV"}
              </CardDescription>
            </CardHeader>
            <CardContent className="py-3 px-4 bg-muted/30">
              <p className="text-sm">{reference.text}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {isAddingNote && selectedReference && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Add Note for {formatReference(selectedReference)}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="prose prose-sm dark:prose-invert">
                  <p>{selectedReference.text}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-note">Your Note</Label>
                  <MarkdownField
                    id="new-note"
                    placeholder="Add your thoughts or insights about this verse..."
                    value={newNote}
                    onValueChange={setNewNote}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={handleCancelAddNote}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveNote} disabled={!newNote.trim()}>
                    Save Note
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}